import { ReadonlyVec3 } from 'gl-matrix';
import { atom, selector, selectorFamily } from 'recoil';
import { StructureSetId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { dicomColorToRgbString } from '~/utils/color';
import { currentStructureSetState } from './structureSetAtoms';

export const defaultGlobalROIContourThickness = 1;

export type SymmetricMargin = {
    type: 'symmetric';
    value: number;
};

export type AsymmetricMargin = {
    type: 'asymmetric';
    left: number;
    right: number;
    top: number;
    bottom: number;
    anterior: number;
    posterior: number;
};

export type MarginValue = SymmetricMargin | AsymmetricMargin | null;

export const defaultMarginValue: MarginValue = null;

export const selectedROIState = atom<number>({
    key: 'selectedROIState',
    default: -1,
});

// Bulk-selected ROI numbers. Invariant: empty, or size >= 2. While non-empty,
// the active ROI (selectedROIState) is kept a member so the list highlight
// can't disagree with the selection. Never written by plain clicks; must be
// reset wherever selectedROIState is reset. One row control does write it: a
// row's 3D-preview button clears the selection, because asking to preview one
// ROI means that ROI and the preview shows a bulk selection in preference to
// the active one.
export const multiSelectedRoiNumbersState = atom<ReadonlySet<number>>({
    key: 'multiSelectedRoiNumbersState',
    default: new Set(),
});

/** Where a point ROI would land if the move in progress were released now. */
export interface UncommittedPointCoordinates {
    roiNumber: number;
    /** Patient coordinates, mm. */
    coordinatesMm: ReadonlyVec3;
}

/**
 * The point move in progress. Display only: it is never persisted, and it is
 * null whenever no move is in progress or the release would leave the point
 * as it is.
 */
export const uncommittedPointCoordinatesState =
    atom<UncommittedPointCoordinates | null>({
        key: 'uncommittedPointCoordinatesState',
        default: null,
    });

// While bulk selection is engaged, contouring and ROI operations stay
// disabled: they act on the active ROI, which is misleading when the user
// is looking at a multi-selection.
/**
 * Multi-select mode: a plain row click toggles membership instead of
 * moving the active structure. Armed by the structure list's control, and
 * independent of how many structures are currently selected — the mode is
 * how an EMPTY selection gets built up, so it cannot be derived from the
 * selection itself.
 */
export const multiSelectModeState = atom<boolean>({
    key: 'multiSelectModeState',
    default: false,
});

/**
 * The structure list is selecting several structures rather than acting on
 * one. True while the mode is armed even before anything is picked: the
 * bulk panel has replaced the structure details by then, and the
 * single-structure tools it gates must not stay live behind it.
 */
export const bulkRoiSelectionActiveState = selector<boolean>({
    key: 'bulkRoiSelectionActiveState',
    get: ({ get }) =>
        get(multiSelectModeState) ||
        get(multiSelectedRoiNumbersState).size >= 2,
});

// True iff the selected ROI's visibility is ephemeral (auto-shown by
// selection, or solo after Hide-all). Step-through reads this to decide
// whether to hide the previous selection on move-away.
export const selectionAutoShownState = atom<boolean>({
    key: 'selectionAutoShownState',
    default: false,
});

/**
 * Soft-isolate display mode: dim every contour except the selected one.
 * Display-only — changes no ROI's `visible` flag and no contour data, so
 * unlike Hide-all there is nothing to snapshot/restore. The engine ignores
 * it without a selection, hence the control lives on the selected row.
 */
export const structureIsolationActiveState = atom<boolean>({
    key: 'structureIsolationActiveState',
    default: false,
});

/** The structure whose contour the slice views leave out while a key is held. */
export interface StructurePeek {
    /** The peeked structure's set: ROI numbers are unique only within a set. */
    readonly structureSetId: StructureSetId;
    readonly roiNumber: number;
}

/**
 * Peek: the active structure's contour is left out of the slice views for as
 * long as a key is held, to show the image beneath it. Display-only and
 * in-memory — changes no ROI's `visible` flag, no selection and no contour
 * data, so ending it has nothing to restore. Null while the key is up. Read
 * {@link currentStructurePeekState} to act on it: this raw value can name a
 * structure that has stopped being peekable.
 */
export const activeStructurePeekState = atom<StructurePeek | null>({
    key: 'activeStructurePeekState',
    default: null,
});

export const openedStructureDeletionConfirmationDialogState = atom<
    number | null
>({
    key: 'openedStructureDeletionConfirmationDialogState',
    default: null,
});

/**
 * When set to a ROI number, triggers inline rename mode for that ROI
 * in the StructureField component. Reset to null after consumption.
 */
export const renamingRoiState = atom<number | null>({
    key: 'renamingRoiState',
    default: null,
});

export const openedStructureSetApprovalConfirmationDialogState = atom<{
    sopUid: string;
    newApprovalStatus: DICOMApprovalStatus;
} | null>({
    key: 'openedStructureSetApprovalConfirmationDialogState',
    default: null,
});

export const globalROIContourThicknessState = atom<number>({
    key: 'globalROIContourThicknessState',
    default: defaultGlobalROIContourThickness,
});

export const roisVisibilityState = selectorFamily({
    key: 'roisVisibilityState',
    get:
        (roiNumbers: number[]) =>
        ({ get }) => {
            const visibilityMap = new Map<number, boolean>();
            const currentStructureSet = get(currentStructureSetState);
            roiNumbers.forEach((roiNumber) => {
                const roiReadModel = currentStructureSet?.roiArray.find(
                    (roi) => roi.roiNumber === roiNumber
                );
                const isVisible = roiReadModel?.visible ?? false;
                visibilityMap.set(roiNumber, isVisible);
            });

            return visibilityMap;
        },
});

export const roiColorsState = selectorFamily({
    key: 'roiColorsState',
    get:
        (roiNumbers: number[]) =>
        ({ get }) => {
            const colorsMap = new Map<number, string>();
            const currentStructureSet = get(currentStructureSetState);
            roiNumbers.forEach((roiNumber) => {
                const roiReadModel = currentStructureSet?.roiArray.find(
                    (roi) => roi.roiNumber === roiNumber
                );
                if (!roiReadModel) {
                    return;
                }
                colorsMap.set(
                    roiNumber,
                    dicomColorToRgbString(roiReadModel.color)
                );
            });

            return colorsMap;
        },
});

// ── Editing one structure's display colour ───────────────────────────

/** The control a colour edit was opened from. */
export type StructureColorEditSource =
    | 'row'
    | 'contextMenu'
    | 'properties'
    | 'footer'
    | 'viewer';

/**
 * The open colour edit; at most one exists. Nothing in it is saved:
 * `pending` is shown wherever the structure's colour is shown and becomes
 * the structure's colour only when the edit is applied.
 */
export interface StructureColorEditSession {
    /**
     * The edited structure's set. The edit closes unsaved when another set
     * becomes current, so it never saves onto a structure of another set
     * that shares its ROI number.
     */
    structureSetId: StructureSetId;
    roiNumber: number;
    source: StructureColorEditSource;
    /** What the popover is placed beside: an element it follows, or a point. */
    anchor: Pick<Element, 'getBoundingClientRect'>;
    /** Receives focus back when the popover closes. */
    returnFocusTo: HTMLElement | null;
    /** The structure's saved colour when the edit opened. */
    colorAtOpen: DicomColor;
    /** A colour being dragged or typed and not yet saved, or null. */
    pending: DicomColor | null;
}

export const structureColorEditSessionState =
    atom<StructureColorEditSession | null>({
        key: 'structureColorEditSessionState',
        default: null,
    });

/**
 * The open colour edit while its structure set is the current one,
 * otherwise null. ROI numbers are unique only within a set, so an edit
 * must never be read against another set's structures.
 */
export const currentSetColorEditState =
    selector<StructureColorEditSession | null>({
        key: 'currentSetColorEditState',
        get: ({ get }) => {
            const session = get(structureColorEditSessionState);
            return session?.structureSetId === get(currentStructureSetState)?.id
                ? session
                : null;
        },
    });

/**
 * The open colour edit when it is this structure's, in the current
 * structure set, otherwise null. Keyed per structure so a drag re-renders
 * only the edited structure's displays.
 */
export const structureColorEditOfState = selectorFamily<
    StructureColorEditSession | null,
    number
>({
    key: 'structureColorEditOfState',
    get:
        (roiNumber) =>
        ({ get }) => {
            const session = get(currentSetColorEditState);
            return session?.roiNumber === roiNumber ? session : null;
        },
});

/** True while any SDF post-processing operation is in flight. */
export const sdfOperationInFlightState = atom<boolean>({
    key: 'sdfOperationInFlightState',
    default: false,
});

export const roisForDvhState = selector({
    key: 'roisForDvhState',
    get: ({ get }) => {
        const currentStructureSet = get(currentStructureSetState);
        if (!currentStructureSet) {
            return [];
        }
        return currentStructureSet.roiArray.filter((r) => r.visible);
    },
});

/** The selected ROI's read model in the current structure set, if any. */
export const selectedRoiReadModelState = selector<RoiReadModel | undefined>({
    key: 'selectedRoiReadModelState',
    get: ({ get }) => {
        const selectedRoiNumber = get(selectedROIState);
        if (selectedRoiNumber === -1) {
            return undefined;
        }
        return get(currentStructureSetState)?.roiArray.find(
            (roi) => roi.roiNumber === selectedRoiNumber
        );
    },
});

export const selectedRoiIsPointTypeState = selector<boolean>({
    key: 'selectedRoiIsPointTypeState',
    get: ({ get }) => get(selectedRoiReadModelState)?.isPointRoi ?? false,
});

/**
 * In-viewer contour comparison: which two ROIs of the current structure set
 * are being compared, and whether compare mode (the overlay + metrics panel)
 * is active.
 */

/** Master on/off for contour comparison mode (overlay + panel). */
export const compareModeActiveState = atom<boolean>({
    key: 'compareModeActiveState',
    default: false,
});

/** ROINumber of ROI A (reference), or null when not yet chosen. */
export const comparisonRoiAState = atom<number | null>({
    key: 'comparisonRoiAState',
    default: null,
});

/** ROINumber of ROI B (test), or null when not yet chosen. */
export const comparisonRoiBState = atom<number | null>({
    key: 'comparisonRoiBState',
    default: null,
});

/**
 * Validate a comparison selection: returns the `[reference, test]` ROI pair
 * when both are chosen and distinct, otherwise null. Centralises the "two
 * distinct ROIs" rule shared by the overlay bridge and the metrics panel, and
 * narrows the nullable atom values for callers.
 */
export function comparablePair(
    roiA: number | null,
    roiB: number | null
): [number, number] | null {
    if (roiA === null || roiB === null || roiA === roiB) {
        return null;
    }
    return [roiA, roiB];
}

/**
 * The structure a peek may withhold, or null. Only the one active structure
 * qualifies: selected in the current set, visible (there is nothing to look
 * under otherwise), not a point (its marker is not a contour), not while a
 * bulk selection has replaced the single active structure, and not while it
 * is one of a compared pair, whose outline the comparison overlay draws
 * whatever the contour layer withholds. Approval and the edit lock are not
 * consulted: a peek changes nothing.
 */
export const peekableStructureState = selector<StructurePeek | null>({
    key: 'peekableStructureState',
    get: ({ get }) => {
        const structureSetId = get(currentStructureSetState)?.id;
        const roi = get(selectedRoiReadModelState);
        const compared = get(compareModeActiveState)
            ? comparablePair(get(comparisonRoiAState), get(comparisonRoiBState))
            : null;
        return structureSetId !== undefined &&
            roi?.visible &&
            !roi.isPointRoi &&
            !get(bulkRoiSelectionActiveState) &&
            !compared?.includes(roi.roiNumber)
            ? { structureSetId, roiNumber: roi.roiNumber }
            : null;
    },
});

/**
 * The peek while it still names the peekable structure, otherwise null: a
 * peek whose structure was deselected, hidden, deleted or left behind by a
 * set change withholds nothing.
 */
export const currentStructurePeekState = selector<StructurePeek | null>({
    key: 'currentStructurePeekState',
    get: ({ get }) => {
        const peek = get(activeStructurePeekState);
        const peekable = get(peekableStructureState);
        return peek?.structureSetId === peekable?.structureSetId &&
            peek?.roiNumber === peekable?.roiNumber
            ? peek
            : null;
    },
});
